import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConstantsService } from '@app/common/constants.service';
import { AgGridConfigurationService } from '@app/shared/services/ag-grid-configuration.service';
import { LoaderService } from '@app/shared/services/loader.service';
import { ReleaseMgmtService } from '@app/shared/services/releaseMgmtService';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridOptions, RowModelType } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { TableLazyLoadEvent } from 'primeng/table';

/**
 * Represents the RollbackMigrationDetailsComponent component.
 */
@Component({
    standalone: true,
    selector: 'app-rollback-migration-details',
    templateUrl: './rollback-migration-details.component.html',
    styleUrls: ['./rollback-migration-details.component.scss'],
    imports: [AgGridModule, CommonModule]
})
/**
 * Represents the RollbackMigrationDetailsComponent component.
 */
export class RollbackMigrationDetailsComponent implements OnInit {
    // input conversation Id
    @Input() releaseLegoRollbackStatusId: string;
    //Default width for the column
    testWidth = 250;
    // Declaration of a GridOptions object
    gridOptions: GridOptions;
    // Declare the columnDefs variable
    columnDefs: any;
    // Declare the agGridColDef variable
    agGridColDef: any;
    // Set the initial page size for pagination
    pageSize = 20;
    // Initialize the selectedRow variable
    selectedRow: any = {};
    // Define the available page size options for pagination
    pageSizeList: number[] = [25, 50, 75, 100];
    // `api` is used to interact with the grid
    api: any;
    // `gridColumnApi` is used to interact with the grid's columns
    public gridColumnApi: any;
    // Declare the agGridRowData variable
    agGridRowData: any;
    // `overlayNoRowsTemplate` is the HTML that is displayed when the grid has no rows
    public overlayNoRowsTemplate = '<span style="font-weight: 400; color: #757575; ">No records found</span>';
    // `cacheBlockSize` is the number of rows fetched per block from the server
    cacheBlockSize: any = 10;
    // `maxBlocksInCache` is the maximum number of blocks allowed in the cache
    maxBlocksInCache: any = 1;
    // `blockLoadDebounceMillis` is the debounce time in milliseconds for block loading
    blockLoadDebounceMillis = 100;
    // Define the row model type
    rowModelType: RowModelType = 'serverSide'   ;
    // Set the style for the chip div
    styleChipDiv = 'width:100%; height: calc(100vh - 130px); flex: 1 1 auto; border: 1px solid #DFDFDF;';
    //Set the server side store type
    serverSideStoreType = 'partial'; // ag grid server side store type
    // Set the loading flag to false
    loading: boolean;
    //Represents the default column definition for the add-view-release-list component.
    public defaultColDef: ColDef = {
        //Specifies whether the column is sortable.
        sortable: true,
        //Specifies whether the column is resizable.
        resizable: true,
        //Specifies whether the column has a filter.
        filter: true,
        //Specifies whether the column can be used for row grouping.
        enableRowGroup: true,
        //Specifies whether the column can be used for value aggregation.
        enableValue: true,
        //Specifies whether the column is editable.
        editable: false,
        //Specifies whether the column height is automatically adjusted based on the content.
        autoHeight: true,
        //Specifies whether the column can be pinned.
        floatingFilter: true,
        //Set initial width for the column
        initialWidth: 200,
        //Set the column width to fill the remaining space
        wrapHeaderText: true,
        //Set the column width to fill the remaining space
        autoHeaderHeight: true
    };

    // `gridDataParams` is used to store grid parameters
    gridDataParams: any;
    /**
     * Represents the RollbackMigrationDetailsComponent.
     */
    constructor(public readonly agGridConfigurationService: AgGridConfigurationService,
        public releaseMgmtService: ReleaseMgmtService,
        private readonly constantsService: ConstantsService, // Service for accessing application constants
        public route: ActivatedRoute,
        public loader: LoaderService,
        public toastr: ToastrService) {
    }

     ngOnInit() {
        const browserZoom = Math.round(window.devicePixelRatio * 100);
        this.testWidth = 300;

        if (browserZoom >= 90 && browserZoom < 100) {
            this.testWidth = 225;
        }
        else if (browserZoom >= 80 && browserZoom < 90) {
            this.testWidth = 250;
        }
        else if (browserZoom >= 70 && browserZoom < 80) {
            this.testWidth = 275;
        }

        // `agGridColDef` is an object that defines the structure and properties of the columns in the ag-Grid table
        this.agGridColDef =
        {
            // `tableName` is a property that specifies the name of the table in the database
            tableName: 'rollbackDatabaseMigrationSummary',
            // `column` is an array of objects, each representing a column in the ag-Grid table with properties like columnName, headerName, dataType, columnType, cellStyle, etc.
            column:
                [
                    // These are the column definitions for the ag-Grid table, each object represents a column with properties like columnName, headerName, dataType, columnType, cellStyle, etc. 
                    { columnName: 'databaseType', headerName: 'DB Type', dataType: 'string', columnType: 'string', cellStyle: 'left', sortable: true, width: 120, editable: false, filter: true },
                    { columnName: 'tenantId', headerName: 'Tenant Id', dataType: 'string', columnType: 'string', cellStyle: 'left', sortable: true, width: 100, editable: false, filter: true },
                    { columnName: 'migrationName', headerName: 'Rollback File Name', dataType: 'string', columnType: 'date', cellStyle: 'left', sortable: true, width: 250, editable: false, filter: true },
                    { columnName: 'status', headerName: 'Status', dataType: 'string', columnType: 'string', cellStyle: 'left', sortable: false, width: 120, editable: false, filter: true },
                    { columnName: 'rollbackCreatedDateTime', headerName: 'Rollback Migration Date', dataType: 'date', columnType: 'date', cellStyle: 'left', sortable: true, width: 200, editable: false },
                    { columnName: 'rollbackComment', headerName: 'Error Details', dataType: 'string', columnType: 'string', cellStyle: 'left', sortable: false, width: 200, editable: false, filter: true }
                ]
        };

        // Added grid options
        this.gridOptions = {
           
        };
        //No pagination
        this.agGridConfigurationService.paginationOn = false;
        // `columnDefs` is set to the column definitions returned by the `getRollbackMigrationSummaryColumnDetails` method of `agGridConfigurationService`
        this.columnDefs = this.agGridConfigurationService.getRollbackMigrationSummaryColumnDetails(this.agGridColDef, false);
    }


    /**
     * Callback function triggered when the ag-Grid is ready.
     * @param params - The parameters passed to the function.
     */
    onGridReady = (params) => {
        if (params != undefined) {
            // `gridDataParams` is set to the parameters passed to the method
            this.gridDataParams = params;
            // `api` is set to the api property of the parameters, which is the ag-Grid API
            this.api = params.api;
            // `gridColumnApi` is set to the columnApi property of the parameters, which is the ag-Grid Column API
            this.gridColumnApi = params.api;
            // `datasource` is set to the result of the `getGridData` method with null as the argument
            const datasource = this.getRollbackDatabaseMigrationSummary(null);
            // The server-side datasource for the ag-Grid is set to `datasource`
            this.api?.setServerSideDatasource(datasource);
        }
    }

    getRollbackDatabaseMigrationSummary(event: TableLazyLoadEvent) {
        return {
            // `getRows` is a method that sets the column definitions for the ag-Grid, makes a request to get the release details list, and sets the row data for the ag-Grid
            getRows: (params) => {
                // The column definitions for the ag-Grid are set to `columnDefs`
                this.api.setColumnDefs(this.columnDefs);
                // `inputRequest` is an object with a `requestParameters` property that is set to the stringified request parameters
                const requestObject = {
                    inputRequest: {
                        requestParameters: JSON.stringify(params.request)
                    },
                    ReleaseLegoRollbackId: this.releaseLegoRollbackStatusId
                };
                // Show the spinner/loader
                this.setLoader();
                // Call the API to get migration summary list
                this.releaseMgmtService.apiPostRollbackDbMigrationSummaryList(requestObject).subscribe({
                    next: (res) => {
                        if (res) {
                            this.agGridRowData = res.data;
                            // The `success` method of the parameters is called with an object that has `rowData` set to `agGridRowData` and `rowCount` set to the total count from the response
                            params.success({
                                rowData: this.agGridRowData,
                                rowCount: res.totalCount,
                            });
                        }
                    },
                    error: (err) => { this.toastr.error('Error while fetching release data', 'Error'); },
                    complete: () => {
                        // Hide the spinner/loader
                        this.setUnLoader();
                    }
                });
            }
        }
    }
    
    /**
     * Sets the page size for pagination in the ag-Grid.
     * @returns void
     */
    setPageSize() {
        // The page size for pagination in the ag-Grid is set to `pageSize`
        this.api.paginationSetPageSize(this.pageSize);
    }

    /**
   * Sets the loader state to indicate that a loading process has started.
   * Also displays the loading spinner.
   */
  setLoader() {
    // `loading` is set to true to indicate that a loading process has started
    this.loading = true;
    // `showSpinner.next(true)` is called on `loaderService` to display the loading spinner
    this.loader.showSpinner.next(true);
  }

  /**
   * Sets the loader state to indicate that the loading process has ended.
   * Hides the loading spinner.
   */
  setUnLoader() {
    // `loading` is set to false to indicate that the loading process has ended
    this.loading = false;
    // `showSpinner.next(false)` is called on `loaderService` to hide the loading spinner
    this.loader.showSpinner.next(false);
  }
}